import React from 'react';
import Icon, { ICONS } from '../icons';
import { useLocalization, type Language } from '../../../config/localization';

/**
 * Lightweight stand-in for a 3D model in list-like surfaces (generation slots,
 * gallery cards, modal thumbnails).
 *
 * Deliberately NOT the real viewer: `@google/model-viewer` spins up a WebGL
 * context per instance, so rendering it in a grid of cards would be ruinous.
 * The interactive viewer lives only in the preview modal — everywhere else
 * shows this icon + badge, and clicking through opens the modal.
 */

/** Filename (with extension) from a model URL, ignoring query string and hash. */
export function modelFilenameFromUrl(url?: string | null): string | null {
  if (!url) return null;
  const clean = url.split('?')[0].split('#')[0];
  const segment = clean.split('/').pop() || '';
  if (!segment) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

interface Model3DPlaceholderProps {
  /** Model URL — only used to derive the displayed filename. */
  url?: string | null;
  language?: Language;
  /**
   * Tight layouts (list rows, modal thumbnails): smaller icon and no filename
   * line. One flag rather than a separate size prop — every call site pairs the
   * two, so splitting them only makes inconsistent combinations expressible.
   */
  compact?: boolean;
  className?: string;
}

export default function Model3DPlaceholder({
  url,
  language = 'en',
  compact = false,
  className = '',
}: Model3DPlaceholderProps) {
  const t = useLocalization(language);
  const filename = modelFilenameFromUrl(url);
  const badge = t.model3d?.badge || '3D';
  const hint = t.model3d?.placeholderHint || 'Open to view the model';

  return (
    <div
      data-testid="model3d-placeholder"
      title={hint}
      className={`w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-cyan-900/40 to-gray-800 p-2 ${className}`}
    >
      <Icon name={ICONS.CUBE_3D} size={compact ? 20 : 32} className="text-cyan-300 flex-shrink-0" />
      <span
        data-testid="model3d-badge"
        className="mt-1.5 px-1.5 py-0.5 rounded bg-cyan-900/70 text-cyan-200 text-[10px] font-semibold tracking-wide"
      >
        {badge}
      </span>
      {!compact && filename && (
        <p
          data-testid="model3d-filename"
          className="mt-1 w-full px-1 text-[10px] text-gray-300 text-center truncate"
          title={filename}
        >
          {filename}
        </p>
      )}
    </div>
  );
}
